import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  CourseraTierSubscriptions_AcquisitionType as AcquisitionType,
  OwnableProduct_BillingCycle,
  CourseraTierSubscriptions_PaymentCadence as PaymentCadence,
  CourseraTierSubscriptions_ProductType as ProductType,
  DescriptionPage_ProfessionalCertificateType as ProfessionalCertificateType,
} from '__generated__/graphql-types';

import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import { useLocation } from 'js/lib/useRouter';
import { get } from 'js/lib/user';

import { useTracker } from '@coursera/event-pulse/react';

import { formatUserFriendlyList } from 'bundles/common/utils/commonUtils';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import { courseraPlusProductVariants } from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import {
  getShouldShowCheckoutPageChanges,
  getShouldShowEnrollmentFlowChanges,
} from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import EnrollmentChoiceTypes, { SUBSCRIBE_TO_COURSERA_PLUS } from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import usePageData from 'bundles/enroll/data/usePageData';
import type { Partner } from 'bundles/enroll/hooks/useGetPartnerNamesByIds';
import {
  getEnrollModalName,
  getEnrollmentOptionsType,
  useEnrollModalEventing,
} from 'bundles/enroll/utils/eventingUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll/utils/useEnrollInS12nSubscription';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import QueryCourseraTierProductConfigurations from 'bundles/payments-common/api/QueryCourseraTierProductConfigurations.graphql';
import QueryProfessionalCertificateType from 'bundles/payments-common/api/QueryProfessionalCertificateType.graphql';
import type {
  QueryCourseraTierProductConfigurationsQuery,
  QueryCourseraTierProductConfigurationsQueryVariables,
} from 'bundles/payments-common/api/__generated__/QueryCourseraTierProductConfigurations';
import type {
  QuerySpecializationPageQuery,
  QuerySpecializationPageQueryVariables,
} from 'bundles/payments-common/api/__generated__/QueryProfessionalCertificateType';
import productTypeObject from 'bundles/payments-common/common/ProductType';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';
import redirectToCheckout from 'bundles/payments-common/utils/redirectToCheckout';
import {
  GOOGLE_PARTNER_IDS,
  IS_FROM_CONSUMER_XDP_PARAM,
  IS_FROM_PRE_PURCHASE_UPSELL_PARAM,
  PRE_PURCHASE_CHECKOUT_PARAM,
} from 'bundles/payments/utils/prePurchaseUpsellCheckoutUtils';
import isGoogleMarketingPage from 'bundles/unified-description-page-common/utils/isGoogleMarketingPage';
import { isPaidMediaXDP } from 'bundles/unified-description-page-common/utils/isPaidMediaXDP';

import fullBar from 'images/coursera-plus/prePurchaseUpsell/fullBar.png';
import singleBar from 'images/coursera-plus/prePurchaseUpsell/singleBar.png';

import _t from 'i18n!nls/coursera-plus';

const LOCAL_STORAGE_PREFIX = 'payments.cartsForPrePurchaseUpsellCheckout';

export const showPrePurchaseUpsellFlow = (pathname?: string) => {
  if (pathname && (isGoogleMarketingPage(pathname) || isPaidMediaXDP(pathname))) {
    return false;
  }
  return getShouldShowEnrollmentFlowChanges();
};

const useQueryCourseraPlusProductConfiguration = (paymentCadence: PaymentCadence, hasFreeTrial: boolean) => {
  const { data } = useQuery<
    QueryCourseraTierProductConfigurationsQuery,
    QueryCourseraTierProductConfigurationsQueryVariables
  >(QueryCourseraTierProductConfigurations, {
    variables: {
      acquisitionType: AcquisitionType.Standard,
      paymentCadence: paymentCadence,
      tier: ProductType.CourseraPlusSubscription,
    },
    context: { clientName: 'gatewayGql' },
  });

  const courseraPlusProudct = data?.CourseraTierSubscriptionsQueries?.queryCourseraTierProductConfigurations?.find(
    (product) => {
      if (
        // Exclude GMM31Io6RjODN9SKOuYz_A (which is a SKU backend created to allow marketing to bypass not having promos in India on RP)
        // untile its removed by the BE
        product.fulfillmentConfiguration.paymentScheme.__typename === 'OwnableProduct_SubscriptionPaymentScheme' &&
        product.fulfillmentConfiguration.legacyProductId?.productItemId !== 'GMM31Io6RjODN9SKOuYz_A'
      ) {
        if (paymentCadence === PaymentCadence.MonthlySubscription) {
          return (
            product.fulfillmentConfiguration.paymentScheme.billingCycle ===
            OwnableProduct_BillingCycle.BillingCycleMonthly
          );
        }
        return product.fulfillmentConfiguration.paymentScheme.billingCycle ===
          OwnableProduct_BillingCycle.BillingCycleAnnual && hasFreeTrial
          ? product.fulfillmentConfiguration.paymentScheme.numberOfTrialDays > 0
          : product.fulfillmentConfiguration.paymentScheme.numberOfTrialDays === 0;
      }
      return false;
    }
  );

  return {
    courseraPlusProudct: courseraPlusProudct
      ? {
          productItemId: courseraPlusProudct?.fulfillmentConfiguration?.legacyProductId?.productItemId,
          price: courseraPlusProudct?.price?.amount,
        }
      : undefined,
  };
};

export const useGetCourseraPlusProductPricesAndConfig = ({
  hasCourseraPlusFreeTrial = true,
}: {
  hasCourseraPlusFreeTrial: boolean;
}) => {
  const { courseraPlusProudct: courseraPlusAnnualProduct } = useQueryCourseraPlusProductConfiguration(
    PaymentCadence.AnnualSubscription,
    hasCourseraPlusFreeTrial
  );
  const { courseraPlusProudct: courseraPlusMonthlyProduct } = useQueryCourseraPlusProductConfiguration(
    PaymentCadence.MonthlySubscription,
    hasCourseraPlusFreeTrial
  );

  const courseraPlusAnnualPriceAmount = courseraPlusAnnualProduct?.price.value
    ? Number(courseraPlusAnnualProduct?.price.value)
    : undefined;
  const courseraPlusMonthlyPriceAmount = courseraPlusMonthlyProduct?.price.value
    ? Number(courseraPlusMonthlyProduct?.price.value)
    : undefined;

  const courseraAnnualAmountAsMonthly = courseraPlusAnnualPriceAmount
    ? Number((courseraPlusAnnualPriceAmount / 12).toFixed(2))
    : null;

  const courseraPlusMonthlyPriceInAYear = courseraPlusMonthlyPriceAmount ? courseraPlusMonthlyPriceAmount * 12 : null;
  const courseraPlusAnnualDiscountPercent =
    courseraPlusMonthlyPriceInAYear &&
    courseraPlusAnnualPriceAmount &&
    Math.ceil(
      ((courseraPlusMonthlyPriceInAYear - courseraPlusAnnualPriceAmount) / courseraPlusMonthlyPriceInAYear) * 100
    );

  const courseraPlusAnnualPriceAsMonthlyDisplay = courseraAnnualAmountAsMonthly ? (
    <ReactPriceDisplay
      value={courseraAnnualAmountAsMonthly}
      currency={courseraPlusAnnualProduct?.price?.currencyCode}
      hideCurrencyCode={true}
    />
  ) : null;

  const courseraPlusMonthlyPriceDisplay = courseraPlusMonthlyPriceAmount ? (
    <ReactPriceDisplay
      value={courseraPlusMonthlyPriceAmount}
      currency={courseraPlusMonthlyProduct?.price?.currencyCode}
      hideCurrencyCode={true}
    />
  ) : null;

  const courseraPlusAnnualPriceDisplay = courseraPlusAnnualPriceAmount ? (
    <ReactPriceDisplay
      value={courseraPlusAnnualPriceAmount}
      currency={courseraPlusAnnualProduct?.price?.currencyCode}
      hideCurrencyCode={true}
    />
  ) : null;

  return {
    courseraPlusAnnualDiscountPercent,
    courseraPlusAnnualPriceAsMonthlyDisplay,
    courseraPlusMonthlyPriceDisplay,
    courseraPlusAnnualPriceDisplay,
    courseraPlusAnnualProductItemId: courseraPlusAnnualProduct?.productItemId,
    courseraPlusMonthlyProductItemId: courseraPlusMonthlyProduct?.productItemId,
    courseraPlusMonthlyProductPrice: courseraPlusMonthlyProduct?.price,
    courseraPlusAnnualProductPrice: courseraPlusAnnualProduct?.price,
    courseraAnnualAmountAsMonthly,
  };
};

export const useGetProductPricesAndConfig = () => {
  const { s12nDerivatives, productPrice, enrollmentAvailableChoices } = usePageData();
  const hasCourseraPlusFreeTrial = enrollmentAvailableChoices.canSubscribeToCourseraPlusFreeTrial;
  const {
    courseraPlusAnnualDiscountPercent,
    courseraPlusAnnualPriceAsMonthlyDisplay,
    courseraPlusMonthlyPriceDisplay,
    courseraPlusAnnualPriceDisplay,
    courseraPlusAnnualProductItemId,
    courseraPlusMonthlyProductItemId,
  } = useGetCourseraPlusProductPricesAndConfig({ hasCourseraPlusFreeTrial });

  const s12nPriceDisplay = s12nDerivatives?.catalogPrice && (
    <ReactPriceDisplay
      value={s12nDerivatives?.catalogPrice?.amount}
      currency={s12nDerivatives?.catalogPrice?.currencyCode}
      hideCurrencyCode={true}
    />
  );

  const standaloneCoursePriceDisplay = productPrice && (
    <ReactPriceDisplay value={productPrice?.amount} currency={productPrice?.currencyCode} hideCurrencyCode={true} />
  );

  return {
    courseraPlusAnnualDiscountPercent,
    courseraPlusAnnualPriceAsMonthlyDisplay,
    courseraPlusMonthlyPriceDisplay,
    courseraPlusAnnualPriceDisplay,
    s12nPriceDisplay,
    standaloneCoursePriceDisplay,
    courseraPlusAnnualProductItemId,
    courseraPlusMonthlyProductItemId,
  };
};

export const icons = {
  SINGLE_BAR: singleBar,
  FULL_BAR: fullBar,
};

export const getFormattedPartnerDisplayName = (partnerList: Array<Partner>): string => {
  const partnerNames = partnerList.map((partner) => partner.name);
  const combinedNames = formatUserFriendlyList(partnerNames);
  return _t('By #{combinedNames}', { combinedNames });
};

const getStorageKey = (cartId: number) => {
  const userId = get().id;
  return `${LOCAL_STORAGE_PREFIX}-${userId}-${cartId}`;
};

const setPrePurchaseUpsellCheckoutCartIds = (monthlyCart: CartsV2, annualCart: CartsV2) => {
  const storageKeyForMonthly = getStorageKey(monthlyCart.id);
  const storageKeyForAnnual = getStorageKey(annualCart.id);
  const storageValue = { monthlyCartId: monthlyCart.id, annualCartId: annualCart.id };

  // save for monthly and annual cart, so that we can still reference the cart ids when learner switches between monthly and annual on checkout
  localStorageEx.setItem(storageKeyForMonthly, storageValue, JSON.stringify);
  localStorageEx.setItem(storageKeyForAnnual, storageValue, JSON.stringify);
};

export const getPrePurchaseUpsellCheckoutCartIds = (cartId: number) => {
  const storageKey = getStorageKey(cartId);
  return localStorageEx.getItem(storageKey, JSON.parse, false);
};

export const useCourseraPlusEnrollment = ({
  courseraPlusProductItemId = courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
  additionalOptions,
  isMonthly = true,
  annualProductItemId,
  isPrePurchaseUpsellAllPlansShown,
}: {
  courseraPlusProductItemId: string;
  annualProductItemId?: string;
  additionalOptions?: Record<string, string | boolean | undefined>;
  isMonthly?: boolean;
  isPrePurchaseUpsellAllPlansShown?: boolean;
}) => {
  const track = useTracker();

  const shouldShowRedesignedCheckoutpage = getShouldShowCheckoutPageChanges();
  const additionalParams = {
    [IS_FROM_PRE_PURCHASE_UPSELL_PARAM]: 'true',
    ...(shouldShowRedesignedCheckoutpage ? { [PRE_PURCHASE_CHECKOUT_PARAM]: 'true' } : {}),
  };

  const enrollInCourseraPlusSubscription = async () => {
    const enrollmentChoiceType = EnrollmentChoiceTypes.SUBSCRIBE_TO_COURSERA_PLUS;
    const productItemId = courseraPlusProductItemId;

    const modalName = getEnrollModalName({ isPrePurchaseUpsellAllPlansShown, isUsingPrePurchaseUpsellFlow: true });
    const enrollmentOptionsType = getEnrollmentOptionsType(enrollmentChoiceType, { productItemId });
    let eventingProduct;
    if (additionalOptions?.s12nId) {
      eventingProduct = {
        id: additionalOptions.s12nId as string,
        type: 's12n' as const,
      };
    } else if (additionalOptions?.courseId) {
      eventingProduct = {
        id: additionalOptions.courseId as string,
        type: 'course' as const,
      };
    }

    if (modalName && enrollmentOptionsType) {
      track('click_button', {
        button: {
          name: 'continue_process',
        },
        modal: {
          name: modalName,
        },
        ...(eventingProduct ? { product: eventingProduct } : {}),
        enrollmentOptions: {
          enrollmentOptionsType,
        },
      });
    }

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    const options = { ...additionalOptions, productItemId };

    if (shouldShowRedesignedCheckoutpage && isMonthly) {
      if (!annualProductItemId) {
        throw new Error('Annual productItemId is required to show prepurchase upsell form on checkout');
      }

      const annualOptions = { ...additionalOptions, productItemId: annualProductItemId };
      const monthlyCartPromise = submitEnrollmentPromise({
        handleSubmitPromise,
        options,
        skipCheckoutRedirect: true,
      });

      const annualCartPromise = submitEnrollmentPromise({
        handleSubmitPromise,
        options: annualOptions,
        skipCheckoutRedirect: true,
      });

      return Promise.all([monthlyCartPromise, annualCartPromise]).then(([monthlyResult, annualResult]) => {
        if (monthlyResult.cart && annualResult.cart) {
          setPrePurchaseUpsellCheckoutCartIds(monthlyResult.cart, annualResult.cart);
          redirectToCheckout(monthlyResult.cart, additionalParams, false);
        }

        return {
          cart: monthlyResult.cart,
        };
      });
    }
    return submitEnrollmentPromise({
      handleSubmitPromise,
      options,
      additionalParams,
    });
  };

  return {
    enrollInCourseraPlusSubscription,
  };
};

export const useCourseraPlusAnnualCheckoutEnrollment = (cart: CartsV2) => {
  const { hasFreeTrial } = cart;
  const { courseraPlusProudct: courseraPlusAnnualProduct } = useQueryCourseraPlusProductConfiguration(
    PaymentCadence.AnnualSubscription,
    hasFreeTrial
  );

  const enrollInCourseraPlusAnnualOnCheckout = async () => {
    const productIdToEnroll = cart.getCourseraPlusProductIdToEnroll();
    let s12nId;
    let courseId;
    if (productIdToEnroll) {
      const productItem = stringKeyToTuple(productIdToEnroll);
      const productType = productItem[0];
      const productId = productItem[1];
      if (productType === productTypeObject.SPECIALIZATION) {
        s12nId = productId;
        courseId = cart.getCourseraPlusCourseIdToGrantMembership();
      } else if (productType === productTypeObject.VERIFIED_CERTIFICATE) {
        courseId = productId;
      }
    }

    const options = {
      courseId,
      s12nId,
      isSubscriptionOnly: Boolean(!courseId && !s12nId),
      productItemId:
        courseraPlusAnnualProduct?.productItemId || courseraPlusProductVariants.ANNUAL_SEVEN_DAY_FREE_TRIAL,
    };

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[SUBSCRIBE_TO_COURSERA_PLUS];

    return submitEnrollmentPromise({
      handleSubmitPromise,
      options,
      skipCheckoutRedirect: true,
    }).then((res) => {
      if (res.cart) {
        setPrePurchaseUpsellCheckoutCartIds(cart, res.cart);
        return {
          cart: res.cart,
        };
      }
    });
  };

  return {
    enrollInCourseraPlusAnnualOnCheckout,
  };
};

export const usePrePurchaseUpsellEnrollment = ({
  isFromS12nSelection,
  courseraPlusProductItemId = courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
  isPrePurchaseUpsellAllPlansShown,
}: {
  isFromS12nSelection?: boolean;
  courseraPlusProductItemId?: string;
  isPrePurchaseUpsellAllPlansShown?: boolean;
}) => {
  const location = useLocation();
  const isUsingPrePurchaseUpsellFlow = true;
  const { enrollInSubscription, canEnrollThroughS12nSubscriptionFreeTrial } = useEnrollInS12nSubscription(
    isFromS12nSelection,
    isUsingPrePurchaseUpsellFlow,
    isPrePurchaseUpsellAllPlansShown
  );
  const { trackEnrollModalContinue } = useEnrollModalEventing({
    isUsingPrePurchaseUpsellFlow,
    isPrePurchaseUpsellAllPlansShown,
  });
  const { s12n, promotionEligibilities, course, enrollmentAvailableChoices } = usePageData();
  const { enrollInCourseraPlusSubscription } = useCourseraPlusEnrollment({
    courseraPlusProductItemId,
    additionalOptions: { courseId: course.id, s12nId: s12n?.id },
    isPrePurchaseUpsellAllPlansShown,
  });
  const shouldShowRedesignedCheckoutpage = getShouldShowCheckoutPageChanges();
  const additionalParams = {
    [IS_FROM_PRE_PURCHASE_UPSELL_PARAM]: 'true',
    ...(shouldShowRedesignedCheckoutpage ? { [PRE_PURCHASE_CHECKOUT_PARAM]: 'true' } : {}),
    ...(!(isGoogleMarketingPage(location.pathname) || isPaidMediaXDP(location.pathname)) &&
    course.partnerIds?.some((partnerId) => GOOGLE_PARTNER_IDS.includes(partnerId))
      ? { [IS_FROM_CONSUMER_XDP_PARAM]: 'true' }
      : {}),
  };

  const enrollInCourse = async () => {
    const enrollmentChoiceType = EnrollmentChoiceTypes.PURCHASE_SINGLE_COURSE;
    trackEnrollModalContinue(enrollmentChoiceType);

    const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities.promoCodeId : undefined;
    const options = { courseId: course.id };

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    return submitEnrollmentPromise({ handleSubmitPromise, options, promoCode, additionalParams });
  };

  return {
    additionalParams,
    enrollInSubscription: () => enrollInSubscription(additionalParams),
    enrollInCourse,
    enrollInCourseraPlusSubscription,
    canEnrollThroughS12nSubscriptionFreeTrial,
    hasCourseraPlusFreeTrial: enrollmentAvailableChoices.canSubscribeToCourseraPlusFreeTrial,
  };
};

export const useGetIsGatewayCertificate = ({ slug }: { slug?: string }) => {
  const { data, loading } = useQuery<QuerySpecializationPageQuery, QuerySpecializationPageQueryVariables>(
    QueryProfessionalCertificateType,
    {
      variables: { input: { slug: slug as string } },
      skip: !slug,
      context: { clientName: 'gatewayGql' },
    }
  );

  return {
    isGatewayCertificate:
      data?.DescriptionPage?.querySpecializationPage?.professionalCertificateType ===
      ProfessionalCertificateType.Gateway,
    loading,
  };
};
